/**
 * Shopify analytics consent for Searchspring. The only module that knows
 * about Shopify's Customer Privacy API. It's asked by:
 * - assets/searchspring-beacon.js, before it loads Beacon.js, sends an event
 *   or adds IDs to an API request, and to clear storage on a refusal. It
 *   also follows withdrawals made in other tabs, and tells
 *   assets/searchspring-platform-events.js when to track the cart;
 * - assets/searchspring-recommendations.js, for the recommend request's
 *   `shopper`: an ID stored with consent goes at once while the API can't
 *   answer yet; otherwise it waits for the API, at most a second;
 * - assets/searchspring-platform-events.js, to start tracking the cart once
 *   the API has answered, and to forget a logged-out shopper.
 *
 * Where consent isn't required, analytics is allowed by default; in consent
 * regions it isn't until the visitor accepts. Fails closed: if the API can't
 * load, or throws, analytics stays off. That isn't a refusal, so nothing is
 * cleared: only consentState() 'refused' clears. Neither is a visitor who
 * hasn't answered the banner yet ('pending').
 * https://shopify.dev/docs/api/customer-privacy
 */

/**
 * The localStorage marker snippets/searchspring-tracking-ids.liquid's
 * persist() writes with consent, and clearSearchspringStorage() removes on a
 * refusal. Shared by every tab, so it carries a decision to pages whose own
 * Shopify consent state is stale (assets/searchspring-beacon.js follows it).
 * That snippet is an inline script and can't import: its CONSENT_KEY must be
 * kept equal to this by hand.
 */
export const CONSENT_MARKER_KEY = 'ssAnalyticsConsent';

/** Beacon.js's stored names (ss* for our site id, athos* for "at…" ones). */
const STORED_NAMES = [
  'UserId',
  'SessionId',
  'ShopperId',
  'ViewedProducts',
  'CartProducts',
  'Attribution',
];

/** @typedef {'allowed' | 'refused' | 'pending' | 'unknown'} ConsentState */

/**
 * The visitor's analytics consent, as the Customer Privacy API reports it.
 *
 * analyticsProcessingAllowed() answers false both for "declined" and for
 * "consent is required and the visitor hasn't answered the banner yet", so
 * currentVisitorConsent() separates them: its `analytics` is 'yes', 'no' or
 * '' (not yet decided). Only 'no' is a refusal. Both states stop analytics,
 * but a page that finds an undecided visitor must not delete what an earlier
 * decision stored — which is also what happens when Shopify's page-embedded
 * consent state lags an acceptance made a moment ago.
 *
 * @returns {ConsentState} 'allowed' for exactly true. For exactly false,
 *   'refused' when the visitor declined analytics (or the API is too old to
 *   say), and 'pending' while they haven't decided. 'unknown' until the API
 *   is on the page, or if it throws or answers anything else. Only 'refused'
 *   clears anything.
 */
export function consentState() {
  try {
    const api = /** @type {any} */ (window).Shopify?.customerPrivacy;
    if (typeof api?.analyticsProcessingAllowed !== 'function') return 'unknown';

    const answer = api.analyticsProcessingAllowed();
    if (answer === true) return 'allowed';
    if (answer === false) {
      // An API without currentVisitorConsent can't tell the two apart; treat
      // false as the refusal it was read as before.
      if (typeof api.currentVisitorConsent !== 'function') return 'refused';

      // Anything that isn't an explicit 'no' — '' (undecided), a missing key,
      // or a 'yes' the other call disagrees with — is not a refusal.
      return api.currentVisitorConsent()?.analytics === 'no' ? 'refused' : 'pending';
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Whether the Customer Privacy API can answer. Without it analytics isn't
 * allowed, but the visitor hasn't refused it either: the API may just have
 * failed to load. An undecided visitor ('pending') is an answer.
 * @returns {boolean}
 */
function consentApiReady() {
  return consentState() !== 'unknown';
}

/**
 * Whether Shopify lets this visitor's storefront use be measured.
 * @returns {boolean} False until the Customer Privacy API has loaded.
 */
export function analyticsAllowed() {
  return consentState() === 'allowed';
}

/** @type {Promise<void> | null} */
let ready = null;

/**
 * Resolves once the Customer Privacy API is on the page, loading it if it
 * isn't. Also resolves if it fails to load, leaving analyticsAllowed() false.
 * @returns {Promise<void>}
 */
export function consentReady() {
  if (ready) return ready;

  ready = new Promise((resolve) => {
    const shopify = /** @type {any} */ (window).Shopify;
    if (consentApiReady() || typeof shopify?.loadFeatures !== 'function') return resolve();

    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => resolve());
    } catch {
      resolve();
    }
  });

  return ready;
}

/**
 * Calls back whenever the visitor's consent changes on this page, e.g. from
 * the cookie banner. The event only prompts a fresh look: the callback gets
 * the API's answer, not the event's claim, so an event saying "granted"
 * while the API refuses can't turn analytics on.
 *
 * The API has been seen to report the new value inside the event. If it
 * ever disagrees with the event, it may not have caught up yet, so it's
 * asked once more a couple of ticks later, after whatever the dispatcher
 * scheduled alongside the event, and the callback gets that answer.
 * @param {(state: ConsentState) => void} callback
 */
export function onConsentChange(callback) {
  const report = () => {
    try {
      callback(consentState());
    } catch {
      // Analytics must never break the page.
    }
  };

  document.addEventListener('visitorConsentCollected', (event) => {
    try {
      const claimed = /** @type {CustomEvent} */ (event).detail?.analyticsAllowed;
      const state = consentState();
      const agrees = typeof claimed !== 'boolean' || state === (claimed ? 'allowed' : 'refused');

      if (agrees) report();
      else setTimeout(() => setTimeout(report, 0), 0);
    } catch {
      // Analytics must never break the page.
    }
  });
}

/**
 * Deletes cookies, in every scope they can have been written with, and
 * localStorage entries of the same names.
 * @param {string[]} names
 */
function removeStored(names) {
  // The parent domain (.jennyyoo.com), this host as a domain, and host-only.
  const host = window.location.hostname;
  const labels = host.split('.');
  const domains = ['', `; domain=.${host}`];
  if (labels.length > 2) domains.push(`; domain=.${labels.slice(1).join('.')}`);

  try {
    const present = document.cookie.split('; ').map((cookie) => cookie.split('=')[0]);

    for (const name of names.filter((name) => present.includes(name))) {
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      }
    }
  } catch {
    // Cookies blocked: nothing was stored there either.
  }

  try {
    for (const name of names) localStorage.removeItem(name);
  } catch {
    // Storage blocked: nothing was stored there either.
  }
}

/**
 * Removes what Searchspring stores about the visitor: Beacon.js's cookies and
 * localStorage, the IDs snippets/searchspring-tracking-ids.liquid persists
 * and its ssAnalyticsConsent marker, and the cart snapshot
 * assets/searchspring-platform-events.js keeps.
 * The theme's own storage (viewedProducts, four-col) is left alone.
 * Call it only on a refusal (consentState() 'refused'), never when the API
 * failed to load or threw, and never for a visitor who hasn't decided yet.
 */
export function clearSearchspringStorage() {
  const names = ['ss', 'athos'].flatMap((prefix) => STORED_NAMES.map((name) => prefix + name));

  removeStored([...names, 'ssPageLoadId', 'athosPageLoadId', CONSENT_MARKER_KEY, 'ssPlatformCart']);
}

/**
 * Forgets the logged-in shopper's ID that Beacon.js stored, for a page with
 * no customer: after a logout, the next person on this browser mustn't be
 * reported as them. A deletion, so it doesn't need consent.
 */
export function forgetShopper() {
  removeStored(['ssShopperId', 'athosShopperId']);
}
